import { ChangeEvent, useState } from 'react';
import styled from 'styled-components';

interface EditableNumberCellProps {
  value: number | null;
  onUpdate: (newValue: number | null) => void;
  placeholder?: string;
  hasError?: boolean;
  /** Reject negative input (used for `weight`, which the DB constrains to >= 0). */
  min?: number;
}

/**
 * Editable numeric cell for the site-locations table (latitude / longitude / weight).
 *
 * Uses `type="text"` + `parseFloat` rather than `type="number"` so the decimal separator
 * is always a period regardless of locale — the same approach the generic
 * editable-percentage-cell takes. We keep the raw typed string in local component state
 * while focused so intermediate values like "-", "12." don't get clobbered by the parsed
 * number on every keystroke; the parsed value is what we push up to the parent. This local
 * string is transient view state for the input element, not a mirror of the row data.
 */
export function EditableNumberCell({ value, onUpdate, placeholder, hasError, min }: EditableNumberCellProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState('');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setInternalValue(text);

    if (text === '') {
      onUpdate(null);
      return;
    }

    const parsed = parseFloat(text);
    if (isNaN(parsed)) return;
    if (min != null && parsed < min) return;
    onUpdate(parsed);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setInternalValue(value != null ? String(value) : '');
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const displayValue = isFocused ? internalValue : value != null ? String(value) : '';

  return (
    <InputCellWrapper $hasError={hasError}>
      <input
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
      />
    </InputCellWrapper>
  );
}

const InputCellWrapper = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;

  input {
    flex: 1;
    min-width: 0;
    font-size: var(--font-size__md);
    padding: 4px 8px;
    border: 1px solid ${props => (props.$hasError ? 'var(--borderColor-danger-emphasis)' : 'var(--borderColor-default)')};
    border-radius: var(--border-radius__xs);

    &:focus-visible {
      outline: var(--focus-ring__width) solid var(--focus-ring__color);
      outline-offset: -1px;
    }
  }
`;
